import { apiClient } from '@/shared/lib/api/apiClient';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  user: {
    id: string;
    email: string;
    name: string;
    roles: string[];
    permissions: string[];
  };
  expiresAt: string;
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export class AuthApiService {
  private static readonly BASE_PATH = '/local-auth';

  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>(
      `${this.BASE_PATH}/login`,
      credentials
    );
  }

  static async logout(): Promise<void> {
    return apiClient.post<void>(`${this.BASE_PATH}/logout`);
  }

  static async refreshToken(): Promise<RefreshTokenResponse> {
    return apiClient.post<RefreshTokenResponse>(
      `${this.BASE_PATH}/refresh`
    );
  }

  static async getProfile(): Promise<UserProfile> {
    return apiClient.get<UserProfile>(`${this.BASE_PATH}/profile`);
  }

  static async updateProfile(
    updates: Partial<Pick<UserProfile, 'name' | 'email'>>
  ): Promise<UserProfile> {
    return apiClient.put<UserProfile>(`${this.BASE_PATH}/profile`, updates);
  }

  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> {
    return apiClient.post<void>(`${this.BASE_PATH}/change-password`, data);
  }

  static async requestPasswordReset(email: string): Promise<void> {
    return apiClient.post<void>(`${this.BASE_PATH}/forgot-password`, { email });
  }

  static async resetPassword(data: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> {
    return apiClient.post<void>(`${this.BASE_PATH}/reset-password`, {
      token: data.token,
      password: data.newPassword,
      confirmPassword: data.confirmPassword,
    });
  }

  static async validateToken(token: string, tokenType?: string): Promise<boolean> {
    const params: Record<string, string> = { token };
    if (tokenType) params.tokenType = tokenType;
    const response = await apiClient.get<{ data: boolean }>(`${this.BASE_PATH}/validate-token`, params);
    return response?.data ?? false;
  }

  static async setPassword(data: {
    token: string;
    password: string;
    confirmPassword: string;
  }): Promise<void> {
    return apiClient.post<void>(`${this.BASE_PATH}/set-password`, data);
  }

  static async checkPermission(permission: string): Promise<{ hasPermission: boolean }> {
    return apiClient.get<{ hasPermission: boolean }>(
      `${this.BASE_PATH}/check-permission/${permission}`
    );
  }

  static async getPermissions(): Promise<string[]> {
    return apiClient.get<string[]>(`${this.BASE_PATH}/permissions`);
  }
}

export default AuthApiService;
